// ============================================================================
// RECEIPT VOUCHER SERVICE — one-step receipt (ADR-0054). Posts on create.
// ============================================================================

import { get, post, del } from '../client/axiosClient';
import { receiptVoucherEndpoints } from '../endpoints/accounts.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse, BackendPagedResult, PaginatedResponse, EmptyResponse, ID,
  ReceiptVoucherDto, CreateReceiptVoucherRequest, ReceiptVoucherQueryParams, OutstandingOpenItemDto, ReceivablePartyDto,
  DeductionReasonDto,
} from '../types';

class ReceiptVoucherService {
  async getAll(params?: ReceiptVoucherQueryParams): Promise<PaginatedResponse<ReceiptVoucherDto>> {
    const endpoint = params ? buildEndpointWithQuery(receiptVoucherEndpoints.list(), params) : receiptVoucherEndpoints.list();
    const response = await get<ApiResponse<BackendPagedResult<ReceiptVoucherDto>>>(endpoint);
    const paged = response.data.data;
    return {
      success: response.data.success, message: response.data.message,
      data: Array.from(paged.items || []),
      pagination: {
        currentPage: paged.page, pageSize: paged.pageSize, totalPages: paged.totalPages,
        totalItems: paged.totalCount, hasNextPage: paged.hasNext, hasPreviousPage: paged.hasPrevious,
      },
    };
  }

  /** Full voucher including its lines — the list response carries headers only. */
  async getById(id: ID): Promise<ReceiptVoucherDto> {
    const response = await get<ApiResponse<ReceiptVoucherDto>>(receiptVoucherEndpoints.getById(id));
    return response.data.data;
  }

  /** What a party owes the entity — invoices, or a supplier's debit balance. Empty = nothing owed. */
  async getOpenItems(partyId: ID): Promise<OutstandingOpenItemDto[]> {
    const url = buildEndpointWithQuery(receiptVoucherEndpoints.openItems(), { partyId });
    const response = await get<ApiResponse<OutstandingOpenItemDto[]>>(url);
    return Array.from(response.data.data || []);
  }

  /** Parties that owe the entity, largest first — the picker's default list. */
  async getReceivableParties(searchTerm?: string, limit = 25): Promise<ReceivablePartyDto[]> {
    const url = buildEndpointWithQuery(receiptVoucherEndpoints.receivableParties(), { searchTerm, limit });
    const response = await get<ApiResponse<ReceivablePartyDto[]>>(url);
    return Array.from(response.data.data || []);
  }

  /** Why a bill can be short-paid (ADR-0060) — the Reason picker. */
  async getDeductionReasons(): Promise<DeductionReasonDto[]> {
    const response = await get<ApiResponse<DeductionReasonDto[]>>(receiptVoucherEndpoints.deductionReasons());
    return Array.from(response.data.data || []);
  }

  async create(data: CreateReceiptVoucherRequest): Promise<ReceiptVoucherDto> {
    const response = await post<ApiResponse<ReceiptVoucherDto>>(receiptVoucherEndpoints.create(), data);
    return response.data.data;
  }

  async cancel(id: ID, rowVersion: number, reason?: string): Promise<EmptyResponse> {
    const url = buildEndpointWithQuery(receiptVoucherEndpoints.cancel(id), reason ? { rowVersion, reason } : { rowVersion });
    const response = await del<EmptyResponse>(url);
    return response.data;
  }
}

export const receiptVoucherService = new ReceiptVoucherService();
export default receiptVoucherService;
